// ligfrontend/src/components/Lig/LigComponent.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { memberAPI, yearsAPI } from "../../services/api";
import MemberCard from "./MemberCard";
import YearSelector from "./YearSelector";
import ConfirmDialog from "../common/ConfirmDialog";
import LoadingSpinner from "../common/LoadingSpinner";
import "./LigComponent.css";

const LigComponent = () => {
  const navigate = useNavigate();

  const [family, setFamily] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "danger",
  });

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    await Promise.all([loadYears(), loadFamily()]);
  };

  const loadFamily = async () => {
    try {
      setLoading(true);
      const familyData = await memberAPI.getFamily();

      if (familyData.length === 0) {
        navigate("/lig/landing");
      } else if (familyData[0].temp) {
        navigate(`/lig/invite/${familyData[0]._id}`);
      } else {
        // Sort family members: family head first, then others
        const sortedFamily = sortFamilyMembers(familyData);
        setFamily(sortedFamily);
      }
    } catch (error) {
      console.error("Error loading family:", error);
    } finally {
      setLoading(false);
    }
  };

  const sortFamilyMembers = (members) => {
    return [...members].sort((a, b) => {
      // Family head comes first
      if (a.isFamilyHead && !b.isFamilyHead) return -1;
      if (!a.isFamilyHead && b.isFamilyHead) return 1;
      // Then sort by name
      return a.name.localeCompare(b.name);
    });
  };

  const loadYears = async () => {
    try {
      setLoading(true);
      const yearsData = await yearsAPI.getYears(false);
      setYears(yearsData);

      if (yearsData.length > 0) {
        setSelectedYear(yearsData[yearsData.length - 1]);
      }
    } catch (error) {
      console.error("Error loading years:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  const deleteMember = (member) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Member",
      message: `Are you sure you want to permanently remove ${member.name} from the family?`,
      type: "danger",
      onConfirm: () => confirmDeleteMember(member),
    });
  };

  const confirmDeleteMember = async (member) => {
    closeConfirmDialog();

    try {
      setLoading(true);

      if (member.temp) {
        await memberAPI.deleteTempMember(member._id);
      } else {
        await memberAPI.deleteMember(member.uname);
      }

      removeMemberFromList(member._id);
    } catch (error) {
      console.error("Error deleting member:", error);
      setConfirmDialog({
        isOpen: true,
        title: "Error",
        message: "Failed to delete member. Please try again.",
        type: "danger",
        onConfirm: closeConfirmDialog,
      });
    } finally {
      setLoading(false);
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: "",
      message: "",
      onConfirm: null,
      type: "danger",
    });
  };

  const removeMemberFromList = (id) => {
    setFamily((prevFamily) => {
      const filtered = prevFamily.filter((m) => m._id !== id);
      return sortFamilyMembers(filtered);
    });
  };

  if (loading) {
    return <LoadingSpinner message="Loading family members..." />;
  }

  return (
    <>
      <div className="lig-container">
        <div className="lig-header">
          <h1 className="lig-title">Life is Good</h1>
          <p className="lig-subtitle">Celebrating family moments</p>
        </div>

        {years.length > 0 && (
          <YearSelector
            years={years}
            selectedYear={selectedYear}
            onYearChange={handleYearChange}
          />
        )}

        <div className="family-grid">
          {family.map((member) => (
            <MemberCard
              key={member._id}
              member={member}
              selectedYear={selectedYear}
              onDelete={() => deleteMember(member)}
            />
          ))}
        </div>

        {family.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">👨‍👩‍👧‍👦</div>
            <h2>No Family Members Yet</h2>
            <p>Start your journey by creating your family!</p>
            <button
              className="btn-primary"
              onClick={() => navigate("/lig/landing")}
            >
              Get Started
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm || closeConfirmDialog}
        onCancel={closeConfirmDialog}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default LigComponent;
